/**
 * PostHog: feature flags, and session replay.
 *
 * ## What this is NOT for
 *
 * Anything that has to be ENFORCED. A flag here decides what the browser
 * draws, and the browser is not in the loop for anything that matters: the API
 * takes any valid Supabase token, and `VITE_SUPABASE_URL` is in the bundle, so
 * a hidden login screen is a curtain rather than a lock. Who may get in is
 * Supabase's signup setting; what the server will accept is `config.py`'s
 * gates. Both outlive a third party being unreachable, which this cannot.
 *
 * ## Optional on purpose
 *
 * With no key set, every call here is a no-op and every flag falls back to the
 * constant beside it. That keeps `npm run dev`, the test suite and CI working
 * on a fresh clone with no account, the same way the fake payment and lock
 * providers do -- and it means a PostHog outage degrades to "the app behaves
 * the way the code says", never to a blank page.
 */
import posthog from 'posthog-js'
import { fromEnv } from './env'

// fromEnv, not `??`: an unset host arrives as '' and would hand PostHog
// `api_host: ''` in exactly the deployment that forgot to set it.
const KEY = fromEnv(import.meta.env.VITE_PUBLIC_POSTHOG_KEY)
const HOST = fromEnv(import.meta.env.VITE_PUBLIC_POSTHOG_HOST) ?? 'https://us.i.posthog.com'

/** Whether PostHog is configured at all. False in dev, in tests and in CI. */
export const analyticsEnabled = Boolean(KEY)

/**
 * Every flag this app reads, with the answer to use when PostHog cannot be
 * asked -- no key, an outage, an ad blocker, a flag nobody has created yet.
 *
 * A fallback is not a default "off". It is what the product DOES when nobody
 * is there to say otherwise, so it is written down rather than left to
 * `undefined` being falsy somewhere three files away.
 */
export const FLAG_FALLBACKS = {
  /**
   * Whether the customer half of the app is open for business.
   *
   * False through the soft launch: the coaches run the schedule by hand while
   * the SA/SAS is registered, and a booking form nobody can complete is worse
   * than no booking form. Flipping it in PostHog opens the customer app with
   * no deploy.
   *
   * Hiding only. Signups are switched off in Supabase, which is what actually
   * stops somebody booking -- see the module docstring.
   */
  'customer-app': false,
} as const

export type FlagName = keyof typeof FLAG_FALLBACKS

export function initAnalytics(): void {
  if (!KEY) return

  posthog.init(KEY, {
    api_host: HOST,
    // The admin screens show names, phone numbers and email addresses, and
    // session replay would otherwise ship all of it to a third party. Masked
    // by default and loosened deliberately, rather than the other way round:
    // the version of this that leaks is the one where somebody forgets.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-private], input, textarea, a[href^="tel:"], a[href^="mailto:"]',
    },
    // Pageviews are captured by hand from the router: TanStack does client-side
    // navigation, so the automatic listener sees one pageview per session.
    capture_pageview: false,
    capture_pageleave: true,
    // A JavaScript error on the coach's iPad currently reaches nobody -- he
    // sees a blank panel, works around it, and mentions it next week if at all.
    // Console errors stay OFF: React logs plenty that is not a crash, and an
    // error tracker nobody trusts is one nobody reads.
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
  })
}

/** Where the person is, for replay and flags. Called once a session exists. */
export function identifyViewer(id: string, email: string | null): void {
  if (!KEY) return
  posthog.identify(id, email ? { email } : undefined)
}

/** Forget them on the way out, so the next person is not the last one. */
export function resetViewer(): void {
  if (!KEY) return
  posthog.reset()
}

export function capturePageview(path: string): void {
  if (!KEY) return
  posthog.capture('$pageview', { $current_url: path })
}

/**
 * Read a flag, falling back to the constant above.
 *
 * Synchronous and therefore possibly stale on the very first paint, which is
 * the trade this makes on purpose: a promise here would put a spinner in front
 * of every gated element, and the fallback is the honest answer until PostHog
 * has spoken.
 */
export function isFlagOn(name: FlagName): boolean {
  if (!KEY) return FLAG_FALLBACKS[name]
  const answer = posthog.isFeatureEnabled(name)
  return answer ?? FLAG_FALLBACKS[name]
}
